<template>
  <q-dialog v-model="show" data-testid="document-detail-dialog" persistent>
    <div class="document-detail-dialog">
      <c-page-section squares>
        <q-card class="full-width">
          <q-card-section>
            <div class="heading">
              {{ document.title }}
              <q-icon name="close" class="close-icon" @click="closeDialog" />
            </div>
          </q-card-section>
          <q-separator />

          <q-card-section squares class="q-mb-lg">
            <div class="row space-between q-mb-lg">
              <div class="col">
                <div>
                  <span>{{ t('document.dialog.fromSender') }}</span>
                </div>
                <div>{{ document.editorFullName }}</div>
              </div>
              <div v-if="document.signedAt" class="col text-right">
                <div>
                  <span>{{ t('document.dialog.signedAt') }}</span>
                </div>
                <div>
                  {{
                    formatDateForTemplate({
                      date: document.signedAt,
                    })
                  }}
                </div>
              </div>
            </div>
            <div class="row space-between q-mb-lg">
              <div class="col">
                <div>
                  <span>{{ t('document.dialog.forRecipient') }}</span>
                </div>
                <div>{{ document.userFullName }}</div>
              </div>
              <div class="col text-right">
                <div>
                  <span>{{ t('document.dialog.sentAt') }}</span>
                </div>
                <div>
                  {{
                    formatDateForTemplate({
                      date: document.sentAt,
                    })
                  }}
                </div>
              </div>
            </div>
            <q-separator />
            <div class="row q-mt-lg q-mb-md">
              <div class="col">
                <wysiwyg-editor-v2 ref="editor" :editable="false" @ready="onEditorReady" />
              </div>
            </div>
          </q-card-section>
          <q-card-actions v-if="showActionButtons" class="dialog-action">
            <c-dialog-form-btns
              :negative-label="t('document.rejectBtn')"
              :positive-label="t('confirmBtn')"
              positive-data-testid="document-sign-button"
              negative-data-testid="document-reject-button"
              @action:negative="onDecline"
              @action:positive="onConfirm"
            ></c-dialog-form-btns>
          </q-card-actions>
        </q-card>
      </c-page-section>
    </div>
  </q-dialog>
  <CConfirmDialog ref="refCConfirmDialog" />
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import CPageSection from '@/components/_common/PageSection.vue';
import CDialogFormBtns from '@/components/_common/DialogFormBtns.vue';
import { hubClient } from '@tfpl/api-client';
import WysiwygEditorV2 from '@/components/_common/WysiwygEditorV2.vue';
import { useDocument } from '@/composables/useDocument';
import CConfirmDialog from '@/components/_common/confirmDialog/ConfirmDialog.vue';
import { formatDateForTemplate } from '@/utils';
import { ConfirmDialogParams } from '@/components/_common/confirmDialog/ConfigDialogParams';
import { useAuthStore } from '@/store/auth';

/*****************************************************************************
 * EMITS
 ****************************************************************************/
const emit = defineEmits(['saved']);

/*****************************************************************************
 * SETUP
 ****************************************************************************/
const DEFAULT_CONFIRM_PARAMS: ConfirmDialogParams = {
  title: '',
  confirmAction: async () => {
    console.debug('this function needs to be replaced');
  },
  confirmBtnLabel: '',
};

const { t } = useI18n();
const authStore = useAuthStore();
const show = ref(false);
const showActionButtons = ref(false);
const { updateDocument } = useDocument();
const refCConfirmDialog = ref(CConfirmDialog);
const confirmParams = ref(DEFAULT_CONFIRM_PARAMS);

/**
 * State
 */
const DEFAULT_DOCUMENT_STATE: hubClient.CreateDocumentDto['documentState'] = hubClient.DocumentState.WAITING_FOR_SIGN;
const SIGNED_DOCUMENT_STATE: hubClient.CreateDocumentDto['documentState'] = hubClient.DocumentState.SIGNED;
const REJECTED_DOCUMENT_STATE: hubClient.CreateDocumentDto['documentState'] = hubClient.DocumentState.REJECTED;

const defaultDocument = {
  uuid: null,
  userUuid: null,
  title: '',
  content: '',
  documentState: DEFAULT_DOCUMENT_STATE,
  sentAt: '',
  signedAt: null,
  editorFullName: '',
  userFullName: '',
};
const document = ref<{
  uuid: string | null;
  documentState: string | null;
  title: string;
  content: string;
  userUuid: string | null;
  sentAt: string;
  signedAt: string | null;
  editorFullName: string | null;
  userFullName: string | null;
}>(defaultDocument);

/**
 * Dialog open / close
 * \@param document
 * @param userDocument
 */

const openDialog = (userDocument: hubClient.DocumentDto) => {
  document.value.userUuid = userDocument.user.uuid;
  document.value.title = userDocument.title;
  document.value.content = userDocument.content;
  document.value.editorFullName = userDocument.editor?.name ?? '';
  document.value.userFullName = userDocument.user.name;
  document.value.sentAt = userDocument.sentAt;
  document.value.signedAt = userDocument.signedAt;
  document.value.uuid = userDocument.uuid;
  show.value = true;
  if (
    authStore.currentUser?.uuid === document.value.userUuid &&
    userDocument.documentState === DEFAULT_DOCUMENT_STATE
  ) {
    showActionButtons.value = true;
  } else {
    showActionButtons.value = false;
  }
};

const closeDialog = () => {
  show.value = false;
};

const onDecline = (event: Event) => {
  if (event) {
    event.stopPropagation();
  }
  if (refCConfirmDialog.value) {
    confirmParams.value.title = t('document.dialog.confirmReject', {
      name: document.value.title,
    });
    confirmParams.value.confirmBtnLabel = t('document.rejectBtn');
    confirmParams.value.confirmAction = async () => {
      handleOnDecline().then(() => {
        refCConfirmDialog.value.closeDialog();
      });
    };
    refCConfirmDialog.value.showDialog(confirmParams.value);
  }
};

const onConfirm = (event: Event) => {
  if (event) {
    event.stopPropagation();
  }
  if (refCConfirmDialog.value) {
    confirmParams.value.title = t('document.dialog.confirmSign', {
      name: document.value.title,
    });
    confirmParams.value.confirmBtnLabel = t('confirmBtn');
    confirmParams.value.confirmAction = async () => {
      handleOnConfirm().then(() => {
        refCConfirmDialog.value.closeDialog();
      });
    };
    refCConfirmDialog.value.showDialog(confirmParams.value);
  }
};

const handleOnConfirm = async () => {
  // update
  if (document.value.uuid) {
    await updateDocument(document.value.uuid, {
      documentState: SIGNED_DOCUMENT_STATE,
      content: document.value.content,
      title: document.value.title,
    });
  }
  emit('saved');
  closeDialog();
};

const handleOnDecline = async () => {
  // update
  if (document.value.uuid) {
    await updateDocument(document.value.uuid, {
      documentState: REJECTED_DOCUMENT_STATE,
      content: document.value.content,
      title: document.value.title,
    });
  }
  emit('saved');
  closeDialog();
};

const editor = ref();
const onEditorReady = () => {
  editor.value.setHTML(document.value.content);
};

/*****************************************************************************
 * EXPOSE
 ****************************************************************************/
defineExpose({
  openDialog,
});
</script>

<style lang="scss" scoped>
@import 'styles/app.variables';

.document-detail-dialog {
  min-width: 60%;
}

@media screen and (max-width: $bp600) {
  .document-detail-dialog {
    min-width: 100%;
  }
}
</style>
